import { emptySave, migrateSave, type SaveBlob } from '@playnets/data';

/**
 * localStorage save, Zod-validated on read (§12.1). A save blob that fails
 * validation is a corrupt save, not a crash — fall back to a fresh one rather
 * than trapping the player on a white screen.
 */
const KEY = 'playnets.save.v1';

/** Jewels for a clear: one for finishing, one more per star past the first. */
export function jewelsFor(stars: number): number {
  return 1 + Math.max(0, stars - 1);
}

export function loadSave(): SaveBlob {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return emptySave();
    const migrated = migrateSave(JSON.parse(raw));
    if (!migrated) {
      console.warn('[playnets] discarding unreadable save');
      return emptySave();
    }
    return migrated;
  } catch {
    return emptySave();
  }
}

export function writeSave(save: SaveBlob): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(save));
  } catch {
    // Private browsing or a full quota. The run still works; it just won't persist.
  }
}

/** 3 stars = no damage, 2 = ≤25% core damage, 1 = cleared (§8). */
export function starsFor(damageTaken: number, coreMax: number): 1 | 2 | 3 {
  if (damageTaken <= 0) return 3;
  if (damageTaken <= coreMax * 0.25) return 2;
  return 1;
}

/**
 * Jewels are only paid for beating your own best on a level, so replaying an
 * easy one isn't a farm.
 */
export function recordClear(save: SaveBlob, levelId: string, stars: number): SaveBlob {
  const best = save.stars[levelId] ?? 0;
  const earned = Math.max(0, jewelsFor(stars) - (best > 0 ? jewelsFor(best) : 0));
  return {
    ...save,
    stars: { ...save.stars, [levelId]: Math.max(best, stars) },
    jewels: save.jewels + earned,
  };
}

export function unlockStructure(save: SaveBlob, id: string, cost: number): SaveBlob {
  if (save.unlockedStructures.includes(id) || save.jewels < cost) return save;
  return {
    ...save,
    jewels: save.jewels - cost,
    unlockedStructures: [...save.unlockedStructures, id],
  };
}
